import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { doCreateStore } from "./redux/store/createStore";
import initInterceptors from "./service/transportInterceptors";
import { AppKitProvider } from './lib/wagmi'
import { TxEngineProvider } from "./tx";
import ErrorBoundary from "./components/Common/ErrorBoundary";
import NetworkGuard from "./components/Common/NetworkGuard/NetworkGuard";
import { CHAIN_CONFIG_ERROR } from "./utils/contractAddresses";
import { installGlobalErrorHandlers } from "./utils/reportError";
// Populates the flow/effect registries before the first render, so a journal
// row recovered at boot can find its verifier immediately.
import "./tx/register";

import "./styles/styles.scss";
import "react-toastify/dist/ReactToastify.css";

// Covers what an error boundary structurally cannot reach: unhandled promise
// rejections and throws from event handlers/timers.
installGlobalErrorHandlers();

const store = doCreateStore();
initInterceptors(store);

if (process.env.REACT_APP_ENVIRONMENT == "PRODUCTION") {
  console.log = () => { };
}

const root = ReactDOM.createRoot(document.getElementById("root"));

// A build whose REACT_APP_CHAIN and REACT_APP_CHAIN_ID contradict each other
// cannot work: wagmi would connect on one chain while every read hook gated on
// `chainId === CHAIN_ID` returned nothing and every signature failed inside
// wagmi with a message naming neither chain. Refuse to mount, and SAY SO —
// utils/contractAddresses.js deliberately reports rather than throws, because a
// throw at import time lands before this file runs and blanks the page silently.
if (CHAIN_CONFIG_ERROR) {
  root.render(
    <div
      role="alert"
      style={{
        maxWidth: 640,
        margin: "80px auto",
        padding: "24px 28px",
        border: "1px solid #ca8a04",
        borderRadius: 12,
        background: "#fef9c3",
        color: "#0f172a",
        font: "16px/1.5 Inter, system-ui, sans-serif",
      }}
    >
      <h1 style={{ fontSize: 20, margin: "0 0 12px" }}>FRDM is misconfigured</h1>
      <p style={{ margin: "0 0 12px" }}>{CHAIN_CONFIG_ERROR}</p>
      <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>
        This is a deployment configuration problem, not a wallet problem. The app
        will not start until the environment is corrected.
      </p>
    </div>
  );
} else {
  root.render(
  // OUTERMOST on purpose: a boundary only catches what is BELOW it, and the
  // providers themselves throw too (wagmi/AppKit init, the tx engine reading a
  // corrupt journal). Anything inside this can fail without blanking the page.
  <ErrorBoundary>
    <Provider store={store}>
      <AppKitProvider>
        {/* Inside AppKitProvider (needs wagmi) and inside the redux Provider
            (needs socket + token for the outbox). Renders no UI of its own. */}
        <TxEngineProvider>
          {/* Mounted beside <App /> rather than inside it: it is a fixed
              overlay that must survive every route, and this keeps App.js's
              router untouched. */}
          <NetworkGuard />
          <App />
          <ToastContainer
            autoClose={2000}
            hideProgressBar={true}
          />
        </TxEngineProvider>
      </AppKitProvider>
    </Provider>
    </ErrorBoundary>
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
